<template>
  <div v-if="post" class="min-h-screen bg-gray-50">
    <!-- Hero Section with Image -->
    <div class="relative h-96">
      <img
        :src="post.image"
        :alt="post.title"
        class="w-full h-full object-cover"
      />
      <div class="absolute inset-0 bg-black/50"></div>
      <div class="absolute inset-0 flex items-center justify-center">
        <div class="container mx-auto px-4 text-center text-white">
          <h1 class="text-4xl md:text-5xl font-bold mb-4">{{ post.title }}</h1>
          <div class="flex items-center justify-center space-x-4 text-sm">
            <span>{{ formatDate(post.date) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="container mx-auto px-4 py-16">
      <div class="max-w-3xl mx-auto">
        <!-- Excerpt -->
        <p class="text-xl text-gray-700 mb-8">{{ post.excerpt }}</p>

        <!-- Content -->
        <div class="prose prose-lg max-w-none">
          <div v-for="(paragraph, index) in contentParagraphs" :key="index" class="mb-6">
            <p class="text-gray-700">{{ paragraph }}</p>
          </div>
        </div>

        <!-- Back to Blog -->
        <div class="text-center mt-12">
          <router-link
            :to="{ name: getRouteName('blog') }"
            class="inline-flex items-center space-x-2 text-[#233862] hover:text-[#3D5A98]"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            <span>
              {{ currentLanguage === 'el' ? 'Πίσω στο Ιστολόγιο' : 'Back to Blog' }}
            </span>
          </router-link>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="container mx-auto px-4 py-8 text-center">
    <p>{{ currentLanguage === 'el' ? 'Το άρθρο δεν βρέθηκε' : 'Post not found' }}</p>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import blogDataEn from '@/data/blog.json'
import blogDataEl from '@/data/blog.el.json'

const route = useRoute()

// Compute current language from route
const currentLanguage = computed(() => {
  return route.path.includes('/el/') ? 'el' : 'en'
})

// Compute route names with language prefix
const getRouteName = (baseName) => {
  return currentLanguage.value === 'el' ? `el${baseName}` : baseName
}

// Import all images
const images = import.meta.glob('@/assets/images/**/*.webp', { eager: true })

// Function to get image URL
const getImageUrl = (imagePath) => {
  try {
    // Remove the @ symbol from the path
    const cleanPath = imagePath.replace('@', '')
    const key = Object.keys(images).find(key => key.includes(cleanPath.split('/').pop()))
    return key ? images[key].default : ''
  } catch (error) {
    console.error('Error loading image:', error)
    return ''
  }
}

const post = computed(() => {
  const posts = currentLanguage.value === 'el' ? blogDataEl.posts : blogDataEn.posts
  const foundPost = posts.find(p => p.id === route.params.id)
  if (foundPost) {
    return {
      ...foundPost,
      image: getImageUrl(foundPost.image)
    }
  }
  return null
})

const contentParagraphs = computed(() => {
  return post.value ? post.value.content.split('\n\n') : []
})

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' }
  return new Date(dateString).toLocaleDateString(
    currentLanguage.value === 'el' ? 'el-GR' : 'en-US',
    options
  )
}

onMounted(() => {
  if (post.value) {
    document.title = `${post.value.title} | Aktoploika`
    updateMetadata()
  }
})

const updateMetadata = () => {
  if (!post.value) return

  // Update meta tags
  const metaTags = {
    'description': post.value.metaDescription || post.value.excerpt,
    'keywords': post.value.keywords?.join(', ') || '',
    'og:title': post.value.title,
    'og:description': post.value.metaDescription || post.value.excerpt,
    'og:type': 'article',
    'og:image': post.value.image,
    'article:published_time': post.value.date
  }

  Object.entries(metaTags).forEach(([name, content]) => {
    let meta = document.querySelector(`meta[name="${name}"]`) ||
               document.querySelector(`meta[property="${name}"]`)
    
    if (!meta) {
      meta = document.createElement('meta')
      if (name.startsWith('og:') || name.startsWith('article:')) {
        meta.setAttribute('property', name)
      } else {
        meta.setAttribute('name', name)
      }
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', content)
  })

  // Add JSON-LD structured data
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    'headline': post.value.title,
    'description': post.value.metaDescription || post.value.excerpt,
    'image': post.value.image,
    'datePublished': post.value.date,
    'inLanguage': currentLanguage.value === 'el' ? 'el' : 'en',
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': window.location.href
    }
  }

  let scriptTag = document.querySelector('script[type="application/ld+json"]')
  if (!scriptTag) {
    scriptTag = document.createElement('script')
    scriptTag.setAttribute('type', 'application/ld+json')
    document.head.appendChild(scriptTag)
  }
  scriptTag.textContent = JSON.stringify(jsonLd)
}
</script>
